// Cabecera de salud del espacio: volumen, totales, espacio recuperable sin
// riesgo, desglose por módulo y por tipo (clicables para filtrar).
import React, { useMemo } from "react";
import { Alert, Button, Skeleton, Tooltip, Typography } from "antd";
import {
  ClockCircleOutlined,
  FileImageOutlined,
  FileOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import type {
  ResumenAlmacenamiento,
  TipoArchivo,
} from "../../../services/almacenamiento.api";
import {
  ETIQUETA_TIPO_PLURAL,
  TEXTO_NIVEL_USO,
  formatDiaChile,
  formatFechaHoraChile,
  formatPeso,
  formatPorcentaje,
  nivelUso,
  porcentaje,
} from "../almacenamientoUtils";

const { Text } = Typography;

const COLORES_MODULO = [
  "var(--violet-600)",
  "var(--orange-500)",
  "var(--blue-400)",
  "var(--pink-500)",
  "var(--success-color)",
  "var(--violet-300)",
  "var(--warning-color)",
  "var(--violet-900)",
];

const ICONO_TIPO: Record<TipoArchivo, React.ReactNode> = {
  image: <FileImageOutlined />,
  video: <VideoCameraOutlined />,
  doc: <FileOutlined />,
};

export interface ResumenSaludProps {
  resumen: ResumenAlmacenamiento | null;
  cargando: boolean;
  refrescando: boolean;
  error: boolean;
  onReescanear: () => void;
  onReintentar: () => void;
  moduloActivo?: string;
  tipoActivo?: TipoArchivo;
  onFiltrarModulo: (modulo: string | undefined) => void;
  onFiltrarTipo: (tipo: TipoArchivo | undefined) => void;
  onVerHuerfanos: () => void;
  onVerRotos: () => void;
}

const ResumenSalud: React.FC<ResumenSaludProps> = ({
  resumen,
  cargando,
  refrescando,
  error,
  onReescanear,
  onReintentar,
  moduloActivo,
  tipoActivo,
  onFiltrarModulo,
  onFiltrarTipo,
  onVerHuerfanos,
  onVerRotos,
}) => {
  const modulos = useMemo(
    () => [...(resumen?.porModulo ?? [])].sort((a, b) => b.bytes - a.bytes),
    [resumen],
  );

  if (cargando && !resumen) {
    return (
      <section className="alm-salud" aria-busy="true" aria-label="Salud del espacio">
        <Skeleton active paragraph={{ rows: 4 }} />
      </section>
    );
  }

  if (!resumen) {
    return (
      <section className="alm-salud" aria-label="Salud del espacio">
        <Alert
          type="error"
          showIcon
          message="No se pudo leer el estado del almacenamiento"
          description={
            error
              ? "Falló la lectura del resumen. Los números no se muestran para no mostrar datos falsos."
              : undefined
          }
          action={
            <Button onClick={onReintentar} loading={cargando}>
              Reintentar
            </Button>
          }
        />
      </section>
    );
  }

  const totalBytesArchivos = resumen.archivos.bytes;
  const volumen = resumen.volumen;
  const pctVolumen = volumen ? porcentaje(volumen.usadoBytes, volumen.totalBytes) : 0;
  const nivel = nivelUso(pctVolumen);
  const pctHuerfanos = porcentaje(resumen.huerfanos.bytes, totalBytesArchivos);

  return (
    <section className="alm-salud" aria-label="Salud del espacio">
      <div className="alm-salud-cab">
        <div>
          <Text strong className="alm-salud-titulo">
            Salud del espacio
          </Text>
          <Text type="secondary" className="alm-salud-escaneo">
            <ClockCircleOutlined /> Último escaneo: {formatFechaHoraChile(resumen.escaneadoEn)}
          </Text>
        </div>
        <Button
          className="alm-btn-tactil"
          icon={<ReloadOutlined />}
          loading={refrescando}
          onClick={onReescanear}
        >
          Reescanear
        </Button>
      </div>

      {error && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="No se pudo actualizar el resumen: estos números pueden estar desactualizados."
          action={<Button onClick={onReintentar}>Reintentar</Button>}
        />
      )}

      {volumen && (
        <div className="alm-volumen">
          <div className="alm-volumen-texto">
            <Text strong>
              {formatPeso(volumen.usadoBytes)} usados de {formatPeso(volumen.totalBytes)}
            </Text>
            <Text type="secondary">
              {" "}
              ({formatPorcentaje(pctVolumen)}) · libres {formatPeso(volumen.libreBytes)}
            </Text>
          </div>
          <div
            className={`alm-barra alm-barra-${nivel}`}
            role="progressbar"
            aria-label="Uso del volumen"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pctVolumen)}
            aria-valuetext={`${formatPorcentaje(pctVolumen)} usado. ${TEXTO_NIVEL_USO[nivel]}`}
          >
            <span className="alm-barra-relleno" style={{ width: `${pctVolumen}%` }} />
          </div>
          <Text className={`alm-volumen-nivel alm-nivel-${nivel}`}>{TEXTO_NIVEL_USO[nivel]}</Text>
        </div>
      )}

      <div className="alm-tarjetitas">
        <div className="alm-tarjetita">
          <Text type="secondary">Archivos</Text>
          <Text strong className="alm-tarjetita-valor">
            {resumen.archivos.cantidad.toLocaleString("es-CL")}
          </Text>
          <Text type="secondary">
            {formatPeso(resumen.archivos.bytes)} en total, con sus miniaturas y versiones livianas
          </Text>
        </div>

        <div className="alm-tarjetita alm-tarjetita-recuperable">
          <Text type="secondary">Recuperable sin riesgo</Text>
          <Text strong className="alm-tarjetita-valor">
            {formatPeso(resumen.huerfanos.bytes)}
          </Text>
          <Text type="secondary">
            {resumen.huerfanos.cantidad.toLocaleString("es-CL")} huérfanos (
            {formatPorcentaje(pctHuerfanos)} de los archivos)
          </Text>
          <Button
            size="small"
            type="link"
            className="alm-btn-link"
            onClick={onVerHuerfanos}
            disabled={resumen.huerfanos.cantidad === 0}
          >
            Revisar huérfanos
          </Button>
        </div>

        <div className="alm-tarjetita">
          <Text type="secondary">Más antiguo</Text>
          <Text strong className="alm-tarjetita-valor">
            {resumen.masAntiguoISO ? formatDiaChile(resumen.masAntiguoISO) : "—"}
          </Text>
          <Text type="secondary">fecha de subida (puede ser estimada)</Text>
        </div>

        <div className="alm-tarjetita">
          <Text type="secondary">Referencias rotas</Text>
          <Text strong className="alm-tarjetita-valor">
            {resumen.referenciasRotas.cantidad.toLocaleString("es-CL")}
          </Text>
          <Text type="secondary">registros cuyo archivo ya no existe</Text>
          <Button
            size="small"
            type="link"
            className="alm-btn-link"
            onClick={onVerRotos}
            disabled={resumen.referenciasRotas.cantidad === 0}
          >
            Ver referencias rotas
          </Button>
        </div>

        {resumen.externos.cantidad > 0 && (
          <Tooltip title="Archivos que viven en un servicio externo (antiguo). No ocupan espacio en este disco y no se pueden eliminar desde aquí.">
            <div className="alm-tarjetita">
              <Text type="secondary">Externos</Text>
              <Text strong className="alm-tarjetita-valor">
                {resumen.externos.cantidad.toLocaleString("es-CL")}
              </Text>
              <Text type="secondary">no se miden</Text>
            </div>
          </Tooltip>
        )}
      </div>

      <div className="alm-desgloses">
        <div className="alm-desglose">
          <Text strong>Por tipo</Text>
          <ul className="alm-lista-tipos">
            {resumen.porTipo.map((t) => {
              const pct = porcentaje(t.bytes, totalBytesArchivos);
              const activo = tipoActivo === t.tipo;
              return (
                <li key={t.tipo}>
                  <button
                    type="button"
                    className={`alm-fila-clic${activo ? " alm-fila-clic-activa" : ""}`}
                    aria-pressed={activo}
                    onClick={() => onFiltrarTipo(activo ? undefined : t.tipo)}
                  >
                    <span className="alm-fila-icono">{ICONO_TIPO[t.tipo]}</span>
                    <span className="alm-fila-nombre">
                      {ETIQUETA_TIPO_PLURAL[t.tipo] ?? t.tipo}
                      <Text type="secondary"> · {t.cantidad.toLocaleString("es-CL")}</Text>
                    </span>
                    <span className="alm-fila-valor">
                      {formatPeso(t.bytes)} <Text type="secondary">({formatPorcentaje(pct)})</Text>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="alm-desglose alm-desglose-modulos">
          <Text strong>Por módulo (toca uno para filtrar)</Text>
          {totalBytesArchivos > 0 && modulos.length > 0 && (
            <div className="alm-apilada" role="img" aria-label="Reparto del peso por módulo">
              {modulos.map((m, i) => (
                <Tooltip
                  key={m.modulo}
                  title={`${m.etiqueta}: ${formatPeso(m.bytes)} (${formatPorcentaje(porcentaje(m.bytes, totalBytesArchivos))})`}
                >
                  <span
                    className="alm-apilada-seg"
                    style={{
                      width: `${porcentaje(m.bytes, totalBytesArchivos)}%`,
                      background: COLORES_MODULO[i % COLORES_MODULO.length],
                    }}
                  />
                </Tooltip>
              ))}
            </div>
          )}
          <ul className="alm-lista-modulos">
            {modulos.map((m, i) => {
              const pct = porcentaje(m.bytes, totalBytesArchivos);
              const activo = moduloActivo === m.modulo;
              return (
                <li key={m.modulo}>
                  <button
                    type="button"
                    className={`alm-fila-clic${activo ? " alm-fila-clic-activa" : ""}`}
                    aria-pressed={activo}
                    onClick={() => onFiltrarModulo(activo ? undefined : m.modulo)}
                  >
                    <span
                      className="alm-punto"
                      style={{ background: COLORES_MODULO[i % COLORES_MODULO.length] }}
                    />
                    <span className="alm-fila-nombre">
                      {m.etiqueta}
                      <Text type="secondary"> · {m.cantidad.toLocaleString("es-CL")}</Text>
                    </span>
                    <span className="alm-fila-valor">
                      {formatPeso(m.bytes)} <Text type="secondary">({formatPorcentaje(pct)})</Text>
                    </span>
                  </button>
                </li>
              );
            })}
            {modulos.length === 0 && <Text type="secondary">Todavía no hay archivos.</Text>}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ResumenSalud;
